// src/pages/LoginPage.tsx
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { PAGE_BG, BTN_PRIMARY, INPUT, ALERT_ERROR } from "../constants/ui";

type LoginResponse = {
  message: string;
  user: {
    id: number;
    name: string;
    username: string;
    email: string;
    role?: string;
  };
  access: string;
  refresh: string;
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [rememberMe, setRememberMe] = useState<boolean>(true);
  const [error, setError] = useState<{
    title: string;
    message: string;
    hint?: string;
  } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // 디버그용 API base (옵션)
  const apiBase = useMemo(() => api.defaults.baseURL ?? "", []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim() || !password) {
      setError({
        title: "입력 오류",
        message: "아이디와 비밀번호를 모두 입력해주세요.",
      });
      return;
    }

    setLoading(true);
    // 에러 메시지는 새로운 에러가 발생하거나 성공할 때만 변경 (즉시 지우지 않음)

    try {
      // ✅ 백엔드: POST /api/auth/login/
      const res = await api.post<LoginResponse>("/auth/login/", {
        username: username.trim(),
        password,
      });

      const { user, access, refresh } = res.data;

      // ✅ 유저 정보 로컬 저장 (role 포함)
      localStorage.setItem("auth_user", JSON.stringify(user));

      // ✅ 토큰 저장 + 인증 상태 true (로그인 유지 옵션 적용)
      login(access, refresh, rememberMe);

      // ✅ 역할에 따른 진입 화면 분기
      if (user.role === "teacher" || user.role === "director") {
        navigate("/teacher", { replace: true });
      } else if (user.role === "parent") {
        navigate("/parent", { replace: true });
      } else {
        // 기본 student
        navigate("/student", { replace: true });
      }
    } catch (err: any) {
      console.error(err);

      const status = err?.response?.status;
      const data = err?.response?.data;

      if (!err?.response) {
        // 네트워크 오류 (서버 자체에 연결 불가)
        setError({
          title: "서버 연결 실패",
          message: "서버에 연결할 수 없습니다.",
          hint: "백엔드 서버가 실행 중인지 확인하거나, 인터넷 연결 상태를 확인해주세요.",
        });
      } else if (status === 400 || status === 401) {
        setError({
          title: "로그인 실패",
          message: data?.detail ?? "아이디 또는 비밀번호가 올바르지 않습니다.",
          hint: "아이디와 비밀번호를 다시 확인해주세요. 계정이 없다면 회원가입을 먼저 진행해주세요.",
        });
      } else if (status === 429) {
        setError({
          title: "로그인 시도 횟수 초과",
          message: "너무 많은 로그인 시도로 일시적으로 차단되었습니다.",
          hint: "잠시 후(약 1분) 다시 시도해주세요.",
        });
      } else if (status >= 500) {
        setError({
          title: "서버 오류",
          message: "서버에서 오류가 발생했습니다.",
          hint: "잠시 후 다시 시도해주세요. 문제가 지속되면 관리자에게 문의해주세요.",
        });
      } else {
        const detail = data?.detail ?? "알 수 없는 오류가 발생했습니다.";
        setError({
          title: "로그인 실패",
          message: detail,
          hint: "문제가 지속되면 관리자에게 문의해주세요.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${PAGE_BG} flex items-center justify-center px-4 py-10`}>
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm ring-1 ring-slate-200/60">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 overflow-hidden rounded-2xl bg-slate-900/5 ring-1 ring-slate-200">
            <img
              src="/logo.png"
              alt="새로운 연구소 로고"
              className="h-full w-full object-cover"
            />
          </div>
          <div>
            <h1 className="text-lg font-semibold leading-tight">로그인</h1>
            <p className="text-xs text-slate-500">
              새로운 연구소 관리자/교사/학생 계정
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleLogin} className="mt-6 grid gap-3">
          <div className="grid gap-1">
            <label className="text-xs font-semibold text-slate-700">
              아이디
            </label>
            <input
              type="text"
              placeholder="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              className={INPUT}
              autoComplete="username"
            />
          </div>

          <div className="grid gap-1">
            <label className="text-xs font-semibold text-slate-700">
              비밀번호
            </label>
            <input
              type="password"
              placeholder="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              className={INPUT}
              autoComplete="current-password"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={loading}
              className="h-4 w-4 rounded border-slate-300 text-teal-700 focus:ring-teal-500"
            />
            <label
              htmlFor="rememberMe"
              className="text-sm text-slate-600 cursor-pointer select-none"
            >
              로그인 유지
            </label>
          </div>

          <button
            type="submit"
            disabled={!username.trim() || !password || loading}
            className={`mt-2 w-full ${BTN_PRIMARY}`}
          >
            {loading ? "로그인 중..." : "로그인"}
          </button>

          {error && (
            <div className={ALERT_ERROR}>
              <p className="font-semibold">{error.title}</p>
              <p className="mt-1">{error.message}</p>
              {error.hint && (
                <p className="mt-2 text-xs opacity-80 border-t border-rose-200 pt-2">
                  {error.hint}
                </p>
              )}
            </div>
          )}
        </form>

        {/* Bottom links */}
        <div className="mt-5 flex items-center justify-between text-sm">
          <Link to="/" className="text-slate-500 hover:underline">
            ← 홈으로
          </Link>

          <Link
            to="/signup"
            className="font-semibold text-[#2aa7aa] hover:underline"
          >
            회원가입
          </Link>
        </div>

        {/* 계정 찾기 링크 */}
        <div className="mt-3 flex items-center justify-center gap-4 text-xs text-slate-400">
          <Link to="/find-username" className="hover:text-[#2aa7aa] hover:underline transition-colors">
            아이디 찾기
          </Link>
          <span>·</span>
          <Link to="/forgot-password" className="hover:text-[#2aa7aa] hover:underline transition-colors">
            비밀번호 재설정
          </Link>
        </div>

        {/* Debug */}
        <div className="mt-4 text-[11px] text-slate-400">
          API: <span className="font-mono">{apiBase}</span>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
